import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Modal from "../../ui/Modal";
import PageState from "../../ui/PageState";
import Select from "../../ui/Select";
import StatusBadge from "../../ui/StatusBadge";
import Textarea from "../../ui/Textarea";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";
import {
  asymmetricSplitLayoutStyle,
  pageHeaderActionsStyle,
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  sectionHeaderStyle,
  surfaceCardBodyStyle,
  surfaceCardHeaderStyle,
  surfaceCardStyle,
  toolbarStyle,
} from "../../ui/pagePatterns";
import {
  createAuditUniverse,
  getAuditUniverse,
  listAudits,
  listAuditUniverse,
  listTaxonomy,
  updateAuditUniverse,
} from "../../api/audit.api";

const cardStyle = {
  ...surfaceCardStyle,
};

const cardHeaderStyle = {
  ...surfaceCardHeaderStyle,
  alignItems: "center",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
};

const splitLayoutStyle = {
  ...asymmetricSplitLayoutStyle,
};

const filterBarStyle = {
  ...surfaceCardBodyStyle,
  ...toolbarStyle,
  borderBottom: `1px solid ${colors.border}`,
};

const listBodyStyle = {
  ...surfaceCardBodyStyle,
  paddingTop: spacing.md,
};

const listPaneStyle = {
  flex: "0 1 440px",
  minWidth: "min(320px, 100%)",
  maxWidth: 520,
};

const detailPaneStyle = {
  flex: "1 1 620px",
  minWidth: "min(360px, 100%)",
};

const embeddedShellStyle = {
  display: "grid",
  gap: spacing.lg,
};

const embeddedHeaderStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: spacing.sm,
  flexWrap: "wrap",
};

const detailBodyStyle = {
  ...surfaceCardBodyStyle,
  display: "grid",
  gap: spacing.lg,
};

const detailTitleBlockStyle = {
  display: "grid",
  gap: spacing.xs / 2,
};

const detailEyebrowStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const detailTitleStyle = {
  margin: 0,
  fontWeight: typography.weight.bold,
  fontSize: typography.size.lg,
  color: colors.text,
};

const detailActionsStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const sectionCardStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surfaceSubtle,
  boxShadow: shadows.xs,
  padding: spacing.md + spacing.sm,
  display: "grid",
  gap: spacing.md,
};

const detailDescriptionStyle = {
  margin: 0,
  color: colors.text,
  fontSize: typography.size.sm,
  lineHeight: 1.55,
  whiteSpace: "pre-wrap",
};

const detailMetaGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: spacing.sm,
};

const sectionHeaderBlockStyle = {
  display: "grid",
  gap: spacing.xs / 2,
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: typography.size.sm,
  fontWeight: typography.weight.bold,
  color: colors.text,
};

const sectionSubtitleStyle = {
  margin: 0,
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const engagementListStyle = {
  display: "grid",
  gap: spacing.sm,
};

const engagementItemStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  padding: spacing.md,
  background: colors.surface,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.md,
  flexWrap: "wrap",
};

const engagementMetaStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const formGridStyle = {
  display: "grid",
  gap: spacing.md,
};

const twoColumnFormGridStyle = {
  display: "grid",
  gap: spacing.md,
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
};

const fieldGroupStyle = {
  display: "grid",
  gap: spacing.xs,
};

const checkboxFieldStyle = {
  display: "flex",
  alignItems: "center",
  gap: spacing.sm,
  fontSize: typography.size.sm,
  color: colors.text,
};

const infoCardStyle = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  padding: `${spacing.sm}px ${spacing.md}px`,
  display: "grid",
  gap: spacing.xs / 2,
};

const infoLabelStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const infoValueStyle = {
  fontWeight: typography.weight.medium,
  color: colors.text,
  fontSize: typography.size.sm,
};

const countValueStyle = {
  fontWeight: typography.weight.bold,
  fontSize: typography.size.lg,
  color: colors.text,
};

const blankEntity = {
  name: "",
  description: "",
  entity_type: "",
  business_area: "",
  audit_priority_override: "",
  next_planned_audit_date: "",
  in_scope: true,
};

export default function AuditUniverseList({ embedded = false }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    entity_type: "",
    risk_rating: "",
    in_scope: "",
    q: "",
    limit: 20,
    offset: 0,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(blankEntity);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState(blankEntity);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["audit-universe", filters],
    queryFn: () =>
      listAuditUniverse({
        entity_type: filters.entity_type || undefined,
        risk_rating: filters.risk_rating || undefined,
        in_scope: filters.in_scope === "" ? undefined : filters.in_scope === "true",
        q: filters.q || undefined,
        limit: filters.limit,
        offset: filters.offset,
      }).then((res) => res.data),
    keepPreviousData: true,
  });

  const detailQuery = useQuery({
    queryKey: ["audit-universe", "detail", selectedId],
    queryFn: () => getAuditUniverse(selectedId).then((res) => res.data),
    enabled: Boolean(selectedId),
  });

  const engagementsQuery = useQuery({
    queryKey: ["audits", { universe_item_id: selectedId }],
    queryFn: () => listAudits({ universe_item_id: selectedId }).then((res) => res.data),
    enabled: Boolean(selectedId),
  });

  const entityTypes = useQuery({
    queryKey: ["taxonomy", "entity_type"],
    queryFn: () => listTaxonomy("entity_type").then((res) => res.data),
  });

  const riskRatings = useQuery({
    queryKey: ["taxonomy", "risk_rating"],
    queryFn: () => listTaxonomy("risk_rating").then((res) => res.data),
  });

  const createMutation = useMutation({
    mutationFn: (payload) => createAuditUniverse(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["audit-universe"]);
      setCreateModalOpen(false);
      setCreateDraft(blankEntity);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateAuditUniverse(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["audit-universe"]);
      queryClient.invalidateQueries(["audit-universe", "detail", selectedId]);
      setEditing(false);
    },
  });

  const selectedItem = useMemo(
    () => detailQuery.data || data?.items?.find((item) => item.id === selectedId) || null,
    [data?.items, detailQuery.data, selectedId]
  );

  React.useEffect(() => {
    if (!selectedId && data?.items?.length) {
      setSelectedId(data.items[0].id);
    }
  }, [data?.items, selectedId]);

  React.useEffect(() => {
    if (selectedItem) {
      setDraft({
        name: selectedItem.name || "",
        description: selectedItem.description || "",
        entity_type: selectedItem.entity_type || "",
        business_area: selectedItem.business_area || "",
        audit_priority_override: selectedItem.audit_priority_override || selectedItem.audit_priority || "",
        next_planned_audit_date: selectedItem.next_planned_audit_date || "",
        in_scope: !!selectedItem.in_scope,
      });
    }
  }, [selectedItem]);

  const onSearch = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, offset: 0 }));
  };

  const saveDraft = () => {
    if (!selectedItem) return;
    updateMutation.mutate({
      id: selectedItem.id,
      payload: {
        name: draft.name || selectedItem.name,
        description: draft.description || null,
        entity_type: draft.entity_type || null,
        business_area: draft.business_area || null,
        audit_priority_override: draft.audit_priority_override || null,
        next_planned_audit_date: draft.next_planned_audit_date || null,
        in_scope: draft.in_scope,
      },
    });
  };

  return (
    <div style={embedded ? embeddedShellStyle : { ...pageShellStyle, gap: spacing.xl }}>
      {!embedded && <Breadcrumbs items={[{ label: "Auditable Entities" }]} />}
      <div style={embedded ? embeddedHeaderStyle : pageHeaderStyle}>
        {!embedded && (
          <div style={pageHeaderTitleBlockStyle}>
            <h1 style={pageTitleStyle}>Auditable Entities</h1>
            <p style={pageSubtitleStyle}>Manage the audit universe used for planning and linked engagements.</p>
          </div>
        )}
        <div style={pageHeaderActionsStyle}>
          <Button onClick={() => setCreateModalOpen(true)} variant="primary">
            + Create Entity
          </Button>
          <Button onClick={() => queryClient.invalidateQueries(["audit-universe"])} variant="secondary">
            Refresh
          </Button>
        </div>
      </div>

      <div style={splitLayoutStyle}>
        <div style={{ ...cardStyle, ...listPaneStyle }}>
          <div style={cardHeaderStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                {!embedded && (
                  <div style={{ fontSize: typography.size.xs, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Auditable Entities
                  </div>
                )}
                <div style={countValueStyle}>{data?.total ?? 0} items</div>
              </div>
              {!embedded && (
                <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>
                  Split view
                </div>
              )}
            </div>
          </div>

          <form onSubmit={onSearch} style={filterBarStyle}>
            <Input
              placeholder="Search name/code"
              value={filters.q}
              onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
              style={{ ...inputStyle, flex: 1, minWidth: 220 }}
            />
            <Select
              value={filters.entity_type}
              onChange={(e) => setFilters((prev) => ({ ...prev, entity_type: e.target.value }))}
              style={{ ...inputStyle, minWidth: 160 }}
            >
              <option value="">Entity Type</option>
              {(entityTypes.data || []).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label || opt.value}
                </option>
              ))}
            </Select>
            <Select
              value={filters.risk_rating}
              onChange={(e) => setFilters((prev) => ({ ...prev, risk_rating: e.target.value }))}
              style={{ ...inputStyle, minWidth: 160 }}
            >
              <option value="">Risk Rating</option>
              {(riskRatings.data || []).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label || opt.value}
                </option>
              ))}
            </Select>
            <Select
              value={filters.in_scope}
              onChange={(e) => setFilters((prev) => ({ ...prev, in_scope: e.target.value }))}
              style={{ ...inputStyle, minWidth: 140 }}
            >
              <option value="">Scope</option>
              <option value="true">In scope</option>
              <option value="false">Out of scope</option>
            </Select>
            <Button type="submit" variant="secondary">
              Apply
            </Button>
          </form>

          <div style={listBodyStyle}>
            {isLoading ? (
              <PageState
                state="loading"
                align="left"
                compact
                title="Loading auditable entities"
                description="Glyder is preparing the planning catalog, classifications, and linked audit context."
              />
            ) : isError ? (
              <PageState
                state="error"
                align="left"
                compact
                title="Auditable entities are unavailable"
                description={`${error?.message || "Glyder could not load auditable entities."} Refresh the workspace to try again.`}
                action={
                  <Button type="button" variant="secondary" onClick={() => queryClient.invalidateQueries(["audit-universe"])}>
                    Refresh
                  </Button>
                }
              />
            ) : (
              <div style={{ display: "grid", gap: 10, maxHeight: "65vh", overflowY: "auto", padding: "6px 2px" }}>
                {data?.items?.length === 0 && (
                  <PageState
                    state="empty"
                    align="left"
                    compact
                    title="No auditable entities yet"
                    description="Add a master auditable entity record to support planning, prioritization, and linked audits."
                  />
                )}
                {data?.items?.map((item) => {
                  const isSelected = item.id === selectedId;
                  return (
                    <Button
                      key={item.id}
                      onClick={() => {
                        setSelectedId(item.id);
                        setEditing(false);
                      }}
                      variant="secondary"
                      style={{
                        textAlign: "left",
                        justifyContent: "flex-start",
                        padding: "12px",
                        borderRadius: 10,
                        border: `1px solid ${isSelected ? colors.tableRowSelectedBorder : colors.border}`,
                        background: isSelected ? colors.tableRowSelected : colors.surface,
                        boxShadow: "none",
                        display: "grid",
                        gap: 6,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ fontWeight: 700, color: colors.text }}>
                          {item.name} <span style={{ color: colors.textMuted }}>({item.universe_code || "N/A"})</span>
                        </div>
                        <span style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                          {item.residual_risk_rating || item.inherent_risk_rating || "-"}
                        </span>
                      </div>
                      <div style={{ fontSize: typography.size.sm, color: colors.textSecondary }}>
                        {item.description || "No description provided."}
                      </div>
                      <div style={{ display: "flex", gap: 12, fontSize: typography.size.xs, color: colors.textMuted, flexWrap: "wrap" }}>
                        <span>Type: {item.entity_type || "N/A"}</span>
                        <span>Priority: {item.audit_priority || item.audit_priority_override || "N/A"}</span>
                        <span>Next: {item.next_planned_audit_date || "N/A"}</span>
                      </div>
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={{ ...cardStyle, ...detailPaneStyle }}>
          <div style={cardHeaderStyle}>
            <div style={detailTitleBlockStyle}>
              <div style={detailEyebrowStyle}>Entity Details</div>
              <h2 style={detailTitleStyle}>{selectedItem?.name || "Select an entity"}</h2>
              {selectedItem && (
                <div style={{ ...toolbarStyle, gap: spacing.xs }}>
                  <StatusBadge value={selectedItem.in_scope ? "In Scope" : "Out of Scope"} />
                  <StatusBadge value={selectedItem.residual_risk_rating || selectedItem.inherent_risk_rating || "Unrated"} />
                </div>
              )}
            </div>
            {selectedItem && (
              <div style={detailActionsStyle}>
                {editing ? (
                  <>
                    <Button variant="primary" onClick={saveDraft} disabled={updateMutation.isLoading}>
                      Save
                    </Button>
                    <Button variant="secondary" onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button variant="secondary" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                )}
              </div>
            )}
          </div>

          <div style={detailBodyStyle}>
            {!selectedItem && (
              <PageState
                state="empty"
                align="left"
                compact
                title="Select an auditable entity"
                description="Choose an item from the planning catalog to review metadata, ownership, and linked audits."
              />
            )}
            {selectedItem && detailQuery.isLoading && !detailQuery.data && (
              <PageState
                state="loading"
                align="left"
                compact
                title="Loading entity details"
                description="Glyder is preparing the selected entity, ownership, and planning details."
              />
            )}
            {selectedItem && detailQuery.isError && (
              <PageState
                state="error"
                align="left"
                compact
                title="Entity details are unavailable"
                description={`${detailQuery.error?.message || "Glyder could not load the selected entity details."} Refresh the workspace to try again.`}
              />
            )}
            {selectedItem && !editing && (
              <>
                <div style={sectionCardStyle}>
                  <p style={detailDescriptionStyle}>
                    {selectedItem.description || "No description provided."}
                  </p>
                </div>
                <div style={sectionCardStyle}>
                  <div style={sectionHeaderBlockStyle}>
                    <h3 style={sectionTitleStyle}>Metadata</h3>
                    <p style={sectionSubtitleStyle}>Core classification and planning attributes.</p>
                  </div>
                  <div style={detailMetaGridStyle}>
                    <Info label="Code" value={selectedItem.universe_code} />
                    <Info label="Entity Type" value={selectedItem.entity_type} />
                    <Info label="Business Area" value={selectedItem.business_area} />
                    <Info label="Priority Override" value={selectedItem.audit_priority_override} />
                    <Info label="Computed Priority" value={selectedItem.audit_priority} />
                    <Info label="Risk Rating" value={selectedItem.residual_risk_rating || selectedItem.inherent_risk_rating} />
                    <Info label="Next Planned" value={selectedItem.next_planned_audit_date} />
                    <Info label="In Scope" value={selectedItem.in_scope ? "Yes" : "No"} />
                  </div>
                </div>
                <div style={sectionCardStyle}>
                  <div style={sectionHeaderBlockStyle}>
                    <h3 style={sectionTitleStyle}>Linked Audits</h3>
                    <p style={sectionSubtitleStyle}>Audits currently associated with this auditable entity.</p>
                  </div>
                  {engagementsQuery.isLoading && (
                    <PageState
                      state="loading"
                      align="left"
                      compact
                      title="Loading linked audits"
                      description="Preparing the audit history associated with this entity."
                    />
                  )}
                  {!engagementsQuery.isLoading && (engagementsQuery.data?.items?.length || 0) === 0 && (
                    <PageState
                      state="empty"
                      align="left"
                      compact
                      title="No audits linked yet"
                      description="Linked audits will appear here as planning items are converted into active audits."
                    />
                  )}
                  {engagementsQuery.data?.items?.length > 0 && (
                    <div style={engagementListStyle}>
                      {engagementsQuery.data.items.map((engagement) => (
                        <div key={engagement.id} style={engagementItemStyle}>
                          <div>
                            <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>{engagement.title}</div>
                            <div style={engagementMetaStyle}>
                              {engagement.status} | Step: {engagement.current_step}
                            </div>
                          </div>
                          <Button variant="secondary" onClick={() => navigate(`/audit/audits/${engagement.id}`)}>
                            Open Audit
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {selectedItem && editing && (
              <div style={sectionCardStyle}>
                <div style={sectionHeaderBlockStyle}>
                  <h3 style={sectionTitleStyle}>Edit Entity</h3>
                  <p style={sectionSubtitleStyle}>Update master details used by audit planning.</p>
                </div>
                <div style={formGridStyle}>
                  <div style={twoColumnFormGridStyle}>
                    <label style={fieldGroupStyle}>
                      <span style={sectionSubtitleStyle}>Name</span>
                      <Input
                        value={draft.name}
                        onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                        placeholder="Name"
                        style={inputStyle}
                      />
                    </label>
                    <label style={fieldGroupStyle}>
                      <span style={sectionSubtitleStyle}>Business Area</span>
                      <Input
                        value={draft.business_area}
                        onChange={(e) => setDraft((prev) => ({ ...prev, business_area: e.target.value }))}
                        placeholder="Business area"
                        style={inputStyle}
                      />
                    </label>
                  </div>
                  <label style={fieldGroupStyle}>
                    <span style={sectionSubtitleStyle}>Description</span>
                    <Textarea
                      value={draft.description}
                      onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Description"
                      rows={4}
                      style={{ ...inputStyle, minHeight: 90 }}
                    />
                  </label>
                  <div style={twoColumnFormGridStyle}>
                    <label style={fieldGroupStyle}>
                      <span style={sectionSubtitleStyle}>Entity Type</span>
                      <Select
                        value={draft.entity_type}
                        onChange={(e) => setDraft((prev) => ({ ...prev, entity_type: e.target.value }))}
                        style={inputStyle}
                      >
                        <option value="">Entity type</option>
                        {(entityTypes.data || []).map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label || opt.value}
                          </option>
                        ))}
                      </Select>
                    </label>
                    <label style={fieldGroupStyle}>
                      <span style={sectionSubtitleStyle}>Priority Override</span>
                      <Input
                        value={draft.audit_priority_override}
                        onChange={(e) => setDraft((prev) => ({ ...prev, audit_priority_override: e.target.value }))}
                        placeholder="Priority override"
                        style={inputStyle}
                      />
                    </label>
                    <label style={fieldGroupStyle}>
                      <span style={sectionSubtitleStyle}>Next Planned Audit Date</span>
                      <Input
                        type="date"
                        value={draft.next_planned_audit_date ? draft.next_planned_audit_date.slice(0, 10) : ""}
                        onChange={(e) => setDraft((prev) => ({ ...prev, next_planned_audit_date: e.target.value }))}
                        style={inputStyle}
                      />
                    </label>
                  </div>
                  <label style={checkboxFieldStyle}>
                    <input
                      type="checkbox"
                      checked={draft.in_scope}
                      onChange={(e) => setDraft((prev) => ({ ...prev, in_scope: e.target.checked }))}
                    />
                    In scope
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Create Auditable Entity"
        description="Add a master auditable entity record used by audit planning."
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!createDraft.name || createMutation.isLoading}
              onClick={() => createMutation.mutate(createDraft)}
            >
              Create Entity
            </Button>
          </>
        }
      >
        <div style={formGridStyle}>
          <label style={fieldGroupStyle}>
            <span style={sectionSubtitleStyle}>Name</span>
            <Input
              value={createDraft.name}
              onChange={(e) => setCreateDraft((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Name"
              style={inputStyle}
            />
          </label>
          <label style={fieldGroupStyle}>
            <span style={sectionSubtitleStyle}>Description</span>
            <Textarea
              value={createDraft.description}
              onChange={(e) => setCreateDraft((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Description"
              rows={4}
              style={{ ...inputStyle, minHeight: 90 }}
            />
          </label>
          <div style={twoColumnFormGridStyle}>
            <label style={fieldGroupStyle}>
              <span style={sectionSubtitleStyle}>Business Area</span>
              <Input
                value={createDraft.business_area}
                onChange={(e) => setCreateDraft((prev) => ({ ...prev, business_area: e.target.value }))}
                placeholder="Business area"
                style={inputStyle}
              />
            </label>
            <label style={fieldGroupStyle}>
              <span style={sectionSubtitleStyle}>Entity Type</span>
              <Select
                value={createDraft.entity_type}
                onChange={(e) => setCreateDraft((prev) => ({ ...prev, entity_type: e.target.value }))}
                style={inputStyle}
              >
                <option value="">Entity type</option>
                {(entityTypes.data || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label || opt.value}
                  </option>
                ))}
              </Select>
            </label>
            <label style={fieldGroupStyle}>
              <span style={sectionSubtitleStyle}>Priority Override</span>
              <Input
                value={createDraft.audit_priority_override}
                onChange={(e) => setCreateDraft((prev) => ({ ...prev, audit_priority_override: e.target.value }))}
                placeholder="Priority override"
                style={inputStyle}
              />
            </label>
            <label style={fieldGroupStyle}>
              <span style={sectionSubtitleStyle}>Next Planned Audit Date</span>
              <Input
                type="date"
                value={createDraft.next_planned_audit_date}
                onChange={(e) => setCreateDraft((prev) => ({ ...prev, next_planned_audit_date: e.target.value }))}
                style={inputStyle}
              />
            </label>
          </div>
          <label style={checkboxFieldStyle}>
            <input
              type="checkbox"
              checked={createDraft.in_scope}
              onChange={(e) => setCreateDraft((prev) => ({ ...prev, in_scope: e.target.checked }))}
            />
            In scope
          </label>
        </div>
      </Modal>
    </div>
  );
}

function Info({ label, value }) {
  const displayValue =
    value == null || (typeof value === "string" && value.trim() === "") ? "-" : value;

  return (
    <div style={infoCardStyle}>
      <div style={infoLabelStyle}>{label}</div>
      <div style={infoValueStyle}>{displayValue}</div>
    </div>
  );
}
